import React from 'react';
import { cn } from '@/utils';

export interface AudioSvgProps extends React.SVGAttributes<SVGSVGElement> {
  /**
   * Size of the audio visualization component
   */
  size?: number | string;

  /**
   * Color of the microphone and waveforms
   */
  color?: string;

  /**
   * Whether to animate the waveforms
   */
  animate?: boolean;

  /**
   * Optional className for custom styling
   */
  className?: string;
}

export const AudioSvg: React.FC<AudioSvgProps> = ({
  size = 'auto',
  color = '#EB1700',
  animate = false,
  className,
  ...props
}) => {
  return (
    <svg
      width={size === 'auto' ? '284' : size}
      height={size === 'auto' ? '50' : size}
      viewBox="0 0 284 50"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={cn('inline-block', className)}
      role="img"
      aria-label="Audio visualization"
      {...props}
    >
      {/* Microphone Icon */}
      <g data-name="Microphone">
        <path
          d="M20 10C17.7909 10 16 11.7909 16 14V20C16 22.2091 17.7909 24 20 24C22.2091 24 24 22.2091 24 20V14C24 11.7909 22.2091 10 20 10Z"
          fill={color}
        />
        <path
          d="M12 18C12.5523 18 13 18.4477 13 19V20C13 23.866 16.134 27 20 27C23.866 27 27 23.866 27 20V19C27 18.4477 27.4477 18 28 18C28.5523 18 29 18.4477 29 19V20C29 24.9706 24.9706 29 20 29V32H23C23.5523 32 24 32.4477 24 33C24 33.5523 23.5523 34 23 34H17C16.4477 34 16 33.5523 16 33C16 32.4477 16.4477 32 17 32H20V29C15.0294 29 11 24.9706 11 20V19C11 18.4477 11.4477 18 12 18Z"
          fill={color}
        />
      </g>

      {/* Waveform 1 */}
      <g data-name="Waveform-1" transform="translate(58, 0)">
        <rect
          x="2"
          y="10"
          width="6"
          height="30"
          rx="3"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
        <rect x="12" y="5" width="6" height="40" rx="3" fill={color} />
        <rect
          x="22"
          y="15"
          width="6"
          height="20"
          rx="3"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
        <rect x="32" y="0" width="6" height="50" rx="3" fill={color} />
        <rect
          x="42"
          y="12"
          width="6"
          height="26"
          rx="3"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
      </g>

      {/* Waveform 2 */}
      <g data-name="Waveform-2" transform="translate(116, 0)">
        <rect x="0" y="8" width="5" height="34" rx="2.5" fill={color} />
        <rect
          x="8"
          y="15"
          width="5"
          height="20"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
        <rect x="16" y="5" width="5" height="40" rx="2.5" fill={color} />
        <rect
          x="24"
          y="18"
          width="5"
          height="14"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
        <rect x="32" y="10" width="5" height="30" rx="2.5" fill={color} />
      </g>

      {/* Waveform 3 (mirrored) */}
      <g data-name="Waveform-3" transform="translate(184, 25) scale(-1, 1)">
        <rect x="0" y="-15" width="5" height="30" rx="2.5" fill={color} />
        <rect
          x="8"
          y="-10"
          width="5"
          height="20"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
        <rect x="16" y="-20" width="5" height="40" rx="2.5" fill={color} />
        <rect
          x="24"
          y="-7"
          width="5"
          height="14"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
      </g>

      {/* Waveform 4 (mirrored) */}
      <g data-name="Waveform-4" transform="translate(220, 25) scale(-1, 1)">
        <rect x="0" y="-25" width="5" height="50" rx="2.5" fill={color} />
        <rect
          x="8"
          y="-10"
          width="5"
          height="20"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
        <rect x="16" y="-20" width="5" height="40" rx="2.5" fill={color} />
        <rect
          x="24"
          y="-12"
          width="5"
          height="24"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
      </g>

      {/* Waveform 5 (mirrored) */}
      <g data-name="Waveform-5" transform="translate(254, 25) scale(-1, 1)">
        <rect x="0" y="-15" width="5" height="30" rx="2.5" fill={color} />
        <rect
          x="8"
          y="-10"
          width="5"
          height="20"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
        <rect x="16" y="-20" width="5" height="40" rx="2.5" fill={color} />
        <rect
          x="24"
          y="-7"
          width="5"
          height="14"
          rx="2.5"
          fill={color}
          className={animate ? 'animate-pulse animate-duration-1000 animate-iteration-count-infinite' : ''}
        />
      </g>
    </svg>
  );
};

AudioSvg.displayName = 'AudioSvg';

export default AudioSvg;
